/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Button from '@react/react-spectrum/Button';
import config from './ErrorStrings';
import {ErrorTypes} from '../enums';
import I18nProvider, {I18nContext, withLocalization} from '@adobe/adobe-intl-react';
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage';
import {Internal} from '@adobe/exc-app/internal';
import * as localeData from './__localization__/ErrorStrings';
import Metrics from '@adobe/exc-app/metrics';
import React, {useState} from 'react';
import './Error.css';

const Svg404 = (
  <svg height="110" viewBox="536.879 -2419.5 135.321 87">
    <g fill="none" stroke="#b3b3b3" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" transform="translate(1161.9 84)">
      <path d="M807.8,60.5v23a2.006,2.006,0,0,0,2,2h118a2.006,2.006,0,0,0,2-2V3.5a2.006,2.006,0,0,0-2-2h-118a2.006,2.006,0,0,0-2,2v27" strokeWidth="3px" transform="translate(-1421 -2503.5)" />
      <line strokeWidth="3px" transform="translate(-622.9 -2467.6)" x2="19.3" y1="19.2" />
      <line strokeWidth="3px" transform="translate(-622.9 -2467.6)" x1="19.3" y1="19.2" />
      <line strokeWidth="2px" transform="translate(-613.3 -2489.5)" x1="122" />
      <line strokeWidth="2px" transform="translate(-605.3 -2495.5)" x1="10" />
    </g>
  </svg>
);

const SvgNoWifi = (
  <svg height="110" viewBox="0 0 141 110">
    <g fill="#b3b3b3" stroke="none">
      <g>
        <path className="cls-1" d="M82.708,87.34473a1.49515,1.49515,0,0,1-1.06055-.43946,21.4615,21.4615,0,0,0-30.35351,0,1.49984,1.49984,0,1,1-2.1211-2.12109,24.4604,24.4604,0,0,1,34.5957,0A1.5,1.5,0,0,1,82.708,87.34473Z" />
        <path className="cls-1" d="M96.44238,74.22559a1.49511,1.49511,0,0,1-1.06054-.43946,40.88839,40.88839,0,0,0-57.82227,0A1.49984,1.49984,0,0,1,35.43848,71.665a43.88728,43.88728,0,0,1,62.06445,0,1.5,1.5,0,0,1-1.06055,2.56055Z" />
        <path className="cls-1" d="M9,47.99561a1.5,1.5,0,0,1-1.06055-2.56055,82.76362,82.76362,0,0,1,82.17871-20.793,1.50007,1.50007,0,0,1-.85742,2.875A79.76359,79.76359,0,0,0,10.06055,47.55615,1.49515,1.49515,0,0,1,9,47.99561Z" />
        <path className="cls-1" d="M110.18164,61.1084a1.49518,1.49518,0,0,1-1.06055-.43945,60.3176,60.3176,0,0,0-85.30078,0,1.49984,1.49984,0,1,1-2.12109-2.1211,63.31653,63.31653,0,0,1,89.543,0,1.5,1.5,0,0,1-1.06055,2.56055Z" />
      </g>
      <circle className="cls-1" cx="66.38403" cy="99.54992" r="3.53741" />
      <g>
        <path className="cls-1" d="M102.33984,38.585a1.5,1.5,0,0,1-1.06054-2.56055L131.45117,5.85205a1.49984,1.49984,0,1,1,2.1211,2.12109L103.40039,38.14551A1.49514,1.49514,0,0,1,102.33984,38.585Z" />
        <path className="cls-1" d="M132.51172,38.585a1.49514,1.49514,0,0,1-1.06055-.43945L101.2793,7.97314a1.49984,1.49984,0,1,1,2.12109-2.12109l30.17188,30.17236a1.5,1.5,0,0,1-1.06055,2.56055Z" />
      </g>
    </g>
  </svg>
);

const SvgCookie = (
  <svg height="110" viewBox="0 0 170 110">
    <g fill="#b3b3b3" stroke="none">
      <path className="cls-1" d="M159.12305,4H18.877A3.87125,3.87125,0,0,0,15,7.85693V40.47419a1.5,1.5,0,0,0,3,0V22H160v80.14258a.86852.86852,0,0,1-.87695.85742H18.877a.86852.86852,0,0,1-.877-.85742V74.47441a1.5,1.5,0,0,0-3,0v27.66817A3.87167,3.87167,0,0,0,18.877,106h140.2461A3.87167,3.87167,0,0,0,163,102.14258V7.85693A3.87125,3.87125,0,0,0,159.12305,4ZM160,20H18V7.85693A.8681.8681,0,0,1,18.877,7h140.2461A.8681.8681,0,0,1,160,7.85693Z" />
      <path className="cls-1" d="M39,13H27a1,1,0,0,0,0,2H39a1,1,0,0,0,0-2Z" />
      <path className="cls-1" d="M26.43945,69.56055a1.49984,1.49984,0,0,0,2.1211-2.1211L18.62109,57.5l9.93946-9.93945a1.49984,1.49984,0,0,0-2.1211-2.1211L16.5,55.37891,6.56055,45.43945a1.49984,1.49984,0,0,0-2.1211,2.1211L14.37891,57.5,4.43945,67.43945a1.49984,1.49984,0,1,0,2.1211,2.1211L16.5,59.62109Z" />
      <path className="cls-1" d="M111.54932,44.86963a7.89264,7.89264,0,0,1-3.084-3.8833A1.4997,1.4997,0,0,0,107.05615,40h-.00146a27.00014,27.00014,0,1,0,26.93652,29.96875,1.50138,1.50138,0,0,0-1.1626-1.62793,7.80948,7.80948,0,0,1-6.11572-7.46973,1.4997,1.4997,0,0,0-1.89355-1.4165,10.32526,10.32526,0,0,1-13.08985-9.94043,10.287,10.287,0,0,1,.437-2.957A1.4996,1.4996,0,0,0,111.54932,44.86963ZM122.084,62.83154a12.99054,12.99054,0,0,0,1.811-.12842,10.79685,10.79685,0,0,0,6.94531,8.17676,24.0718,24.0718,0,1,1-24.7666-27.85937,10.9456,10.9456,0,0,0,2.95166,3.71728,13.20249,13.20249,0,0,0-.2959,2.77637A13.351,13.351,0,0,0,122.084,62.83154Z" />
      <path className="cls-1" d="M97,53a6,6,0,1,0,6,6A6.00657,6.00657,0,0,0,97,53Zm0,9a3,3,0,1,1,3-3A3.00328,3.00328,0,0,1,97,62Z" />
      <path className="cls-1" d="M97,72a5,5,0,1,0,5,5A5.00588,5.00588,0,0,0,97,72Zm0,7a2,2,0,1,1,2-2A2.00229,2.00229,0,0,1,97,79Z" />
      <path className="cls-1" d="M108,81a5,5,0,1,0,5-5A5.00588,5.00588,0,0,0,108,81Zm7,0a2,2,0,1,1-2-2A2.00229,2.00229,0,0,1,115,81Z" />
      <path className="cls-1" d="M105,67a5,5,0,1,0,5-5A5.00588,5.00588,0,0,0,105,67Zm5-2a2,2,0,1,1-2,2A2.00229,2.00229,0,0,1,110,65Z" />
      <path className="cls-1" d="M128,45a5,5,0,1,0-5,5A5.00588,5.00588,0,0,0,128,45Zm-7,0a2,2,0,1,1,2,2A2.002,2.002,0,0,1,121,45Z" />
      <circle className="cls-1" cx="136" cy="50" r="3" />
      <path className="cls-1" d="M71.99561,51.00049a6.00126,6.00126,0,1,0-1.84327-.292A6.01137,6.01137,0,0,0,71.99561,51.00049ZM69.146,44.07617A2.9998,2.9998,0,1,1,71.07617,47.854,3.00446,3.00446,0,0,1,69.146,44.07617Z" />
      <path className="cls-1" d="M75.85352,75.5a5.96629,5.96629,0,0,0,.93505-.07422,1.50018,1.50018,0,0,0-.47265-2.96289,2.90811,2.90811,0,1,1-.919-5.74316,1.50018,1.50018,0,1,0-.47265-2.96289A5.90863,5.90863,0,0,0,75.85352,75.5Z" />
      <path className="cls-1" d="M64.53955,55.24268a5.0005,5.0005,0,1,0,2.91309,2.48242A4.97341,4.97341,0,0,0,64.53955,55.24268Zm.36328,5.373a1.99914,1.99914,0,1,1-.12158-1.52588A1.98825,1.98825,0,0,1,64.90283,60.61572Z" />
      <path className="cls-1" d="M92.3501,35.89893a27.74556,27.74556,0,0,0-7.34864-3.583c-14.43017-4.57666-29.979,3.21338-34.65429,17.36377a26.3658,26.3658,0,0,0,1.57519,20.41015A27.18244,27.18244,0,0,0,68.06689,83.68457,27.928,27.928,0,0,0,76.52734,85.001q1.19166,0,2.38916-.10254a1.49961,1.49961,0,1,0-.25586-2.98828,24.98055,24.98055,0,0,1-9.6875-1.085A24.20349,24.20349,0,0,1,54.59375,68.72461a23.385,23.385,0,0,1-1.398-18.10352C57.3584,38.023,71.22021,31.09375,84.09521,35.17529a24.76538,24.76538,0,0,1,6.55469,3.19532,1.5,1.5,0,0,0,1.7002-2.47168Z" />
    </g>
  </svg>
);

function toCamelCase(str) {
  return str.replace(/([\s|\-|.|_])(\w)/g, (match, separator, letter) => letter.toUpperCase());
}

function Illustration({formatMessage, type = ErrorTypes.UNKNOWN}) {
  const metrics = Metrics.create('exc.core.Error');
  const key = `error.${type.toLowerCase()}`;
  const [instanceId, setInstanceId] = useState();

  Internal.configureMetrics().then(metricsConfig => setInstanceId(metricsConfig?.instanceId));
  const description = (
    <div className="errorPage-description-container">
      <p>{formatMessage(Object.assign(config[`${key}.description`], {values: {instanceId}}))}</p>
      {config[`${key}.refreshBtn`] && (
        <Button
          element="button"
          label={formatMessage(config[`${key}.refreshBtn`])}
          onClick={() => {
            metrics.info(`User refresh click for type: ${type}`);
            window.location.reload();
          }}
          style={{marginTop: '10px'}}
          variant="cta"
        />
      )}
    </div>
  );

  let svg = Svg404;

  if (type === ErrorTypes.OFFLINE) {
    svg = SvgNoWifi;
  } else if (type === ErrorTypes.COOKIE_NEEDED) {
    svg = SvgCookie;
  }

  const heading = formatMessage(config[`${key}.heading`]);

  React.useEffect(() => {
    const divider = ' | ';
    const parts = document.title.split(divider);
    document.title = `${heading}${divider}${parts[parts.length - 1]}`;
  }, []);

  return (
    <IllustratedMessage
      className={toCamelCase(type.toLowerCase())}
      description={description}
      heading={heading}
      illustration={svg}
    />
  );
}

const LocalizedIllustration = withLocalization(Illustration, {displayName: 'Illustration'});

const Error = React.memo(props => {
  const {locale} = React.useContext(I18nContext);
  return (
    <I18nProvider locale={locale} localeData={localeData}>
      <div className="error-message-container">
        <LocalizedIllustration {...props} />
      </div>
    </I18nProvider>
  );
});
Error.displayName = 'Error';
export default Error;
